import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Sidebar.css";
import useApprovalAccess from "../../hooks/useApprovalAccess";
import useAssetAccess from "../../hooks/useAssetAccess";
import useLeaveAccess from "../../hooks/useLeaveAccess";
import usePerformanceAccess from "../../hooks/usePerformanceAccess";
import usePermissions from "../../hooks/usePermissions";
import { visibleAdminSections } from "../../pages/admin/adminMenu";

/* ===================== MENU CONFIG ===================== */
// `match` decides when a link is highlighted (defaults to an exact path match),
// so detail pages like /employee/123 still highlight their menu item.
const isEmployeeDetail = (p) =>
  /^\/employee\/[^/]+$/.test(p) &&
  !p.startsWith("/employee/import") &&
  p !== "/employee/approvers";

const employeesGroup = ({ canImport, canSetApprovers }) => ({
  key: "employees",
  label: "Employees",
  icon: "bi-people",
  sections: [
    {
      items: [
        {
          to: "/employee",
          label: "Employee List",
          match: (p) => p === "/employee" || isEmployeeDetail(p),
        },
        ...(canImport
          ? [
              {
                to: "/employee/import",
                label: "Import Employees",
                match: (p) => p.startsWith("/employee/import"),
              },
            ]
          : []),
        ...(canSetApprovers
          ? [{ to: "/employee/approvers", label: "Per-employee Approvers" }]
          : []),
      ],
    },
  ],
});

// Review screens for approvers; allocation, replenish and setup need COMPANY scope
const leaveGroup = ({
  canReviewLeave,
  canFile,
  companyWide,
  pendingCount,
}) => ({
  key: "leave",
  label: "Leave Management",
  icon: "bi-calendar-check",
  sections: [
    {
      items: [
        ...(canReviewLeave
          ? [
              {
                to: "/leave/approvals",
                label: "Leave Approvals",
                badge: pendingCount,
              },
            ]
          : []),
        ...(canReviewLeave && canFile
          ? [{ to: "/leave/file", label: "File Leave" }]
          : []),
        ...(companyWide
          ? [
              { to: "/leave/allocation", label: "Leave Allocation" },
              { to: "/leave/replenish", label: "Replenish Runs" },
            ]
          : []),
        {
          to: "/leave-management",
          label: "Employee Leave",
          match: (p) =>
            p === "/leave-management" || p.startsWith("/leave/employee/"),
        },
      ],
    },
    {
      title: "Setup",
      items: companyWide
        ? [
            { to: "/leave-type", label: "Leave Types" },
            { to: "/leave-config", label: "Leave Policies" },
          ]
        : [],
    },
  ],
});

// Asset management (assets: view); asset types need COMPANY scope
const assetGroup = (companyWide) => ({
  key: "assets",
  label: "Asset Management",
  icon: "bi-pc-display",
  sections: [
    {
      items: [
        {
          to: "/assets",
          label: "Asset Register",
          match: (p) =>
            p === "/assets" ||
            (/^\/assets\/[^/]+$/.test(p) && p !== "/assets/assignments"),
        },
        { to: "/assets/assignments", label: "Assignments" },
        ...(companyWide
          ? [{ to: "/settings/asset-types", label: "Asset Types" }]
          : []),
      ],
    },
  ],
});

// Performance evaluation: evaluations (COMPANY scope, or branch scope with evaluations); templates and reports: COMPANY scope
const performanceGroup = ({ canSeeEvaluations, canManageTemplates }) => ({
  key: "performance",
  label: "Performance",
  icon: "bi-graph-up-arrow",
  sections: [
    {
      items: [
        ...(canSeeEvaluations
          ? [
              {
                to: "/performance/evaluations",
                label: "Evaluations",
                match: (p) => p.startsWith("/performance/evaluations"),
              },
            ]
          : []),
        ...(canManageTemplates
          ? [
              {
                to: "/performance/templates",
                label: "Evaluation Templates",
                match: (p) => p.startsWith("/performance/templates"),
              },
              { to: "/performance/reports", label: "Reports" },
            ]
          : []),
      ],
    },
  ],
});

// Time (attendance / DTR / overtime / export in one module, like My Time) and
// the one-step approval queues; setup needs COMPANY scope
const attendanceGroup = ({
  can,
  companyWide,
  canSeeApprovals,
  validationCount,
  overtimeCount,
  correctionCount,
  clientVisitCount,
}) => {
  const tabIs = (tab) => (p, search) =>
    p === "/approvals" && search.includes(`tab=${tab}`);
  // Time tabs; no tab = the first one the role can see (attendance first)
  const timeTab = (tab, isFirst) => (p, search) =>
    p === "/time" &&
    (search.includes(`tab=${tab}`) || (isFirst && !search.includes("tab=")));
  const timeTabs = [
    can("attendance") && { tab: "attendance", label: "Attendance" },
    can("dtr") && { tab: "dtr", label: "DTR Entries" },
    can("overtime") && { tab: "overtime", label: "Overtime" },
    companyWide("dtr") && { tab: "export", label: "Export" },
  ].filter(Boolean);
  return {
    key: "attendance",
    label: "Attendance & DTR",
    icon: "bi-clock-history",
    sections: [
      {
        items: timeTabs.map((t, i) => ({
          to: `/time?tab=${t.tab}`,
          label: t.label,
          match: timeTab(t.tab, i === 0),
        })),
      },
      {
        title: "Approvals",
        items: canSeeApprovals
          ? [
              ...(can("attendance")
                ? [
                    {
                      to: "/approvals?tab=validation",
                      label: "Check-ins to validate",
                      badge: validationCount,
                      match: tabIs("validation"),
                    },
                    {
                      to: "/approvals?tab=client-visits",
                      label: "Client visits",
                      badge: clientVisitCount,
                      match: tabIs("client-visits"),
                    },
                  ]
                : []),
              ...(can("overtime")
                ? [
                    {
                      to: "/approvals?tab=overtime",
                      label: "Overtime requests",
                      badge: overtimeCount,
                      match: tabIs("overtime"),
                    },
                  ]
                : []),
              ...(can("dtr")
                ? [
                    {
                      to: "/approvals?tab=corrections",
                      label: "Attendance corrections",
                      badge: correctionCount,
                      match: tabIs("corrections"),
                    },
                  ]
                : []),
            ]
          : [],
      },
      {
        title: "Setup",
        items: [
          ...(companyWide("attendance")
            ? [
                { to: "/attendance/settings", label: "Attendance Settings" },
                { to: "/attendance/biometrics", label: "Biometrics" },
              ]
            : []),
        ],
      },
    ],
  };
};

// Learning (view; editing is company-wide HR only)
const LEARNING_GROUP = {
  key: "learning",
  label: "Learning",
  icon: "bi-mortarboard",
  sections: [
    {
      items: [
        {
          to: "/learning/seminars",
          label: "Seminars & Trainings",
          match: (p) => p.startsWith("/learning/seminars"),
        },
        { to: "/learning/bonds", label: "Training Bonds" },
        { to: "/learning/skills", label: "Skills Catalog" },
      ],
    },
  ],
};

// Administration: the same sections and items as the /admin hub
const adminGroup = (sections) => ({
  key: "admin",
  label: "Administration",
  icon: "bi-shield-lock",
  sections: [
    { items: [{ to: "/admin", label: "Overview" }] },
    ...sections.map((sec) => ({
      title: sec.title,
      items: sec.items.map(({ to, label, match }) => ({ to, label, match })),
    })),
  ],
});

const isItemActive = (item, path, search = "") =>
  item.match ? item.match(path, search) : path === item.to;
const isGroupActive = (group, path, search) =>
  group.sections.some((s) =>
    s.items.some((item) => isItemActive(item, path, search)),
  );

/* ===================== COMPONENTS ===================== */
const NavGroup = ({ group, path, search, isOpen, isCollapsed, onToggle }) => {
  const active = isGroupActive(group, path, search);
  return (
    <div className="sidebar-group">
      <button
        type="button"
        className={`sidebar-link sidebar-group-toggle ${active ? "is-current" : ""}`}
        onClick={onToggle}
        aria-expanded={!isCollapsed && isOpen}
        title={isCollapsed ? group.label : undefined}
      >
        <span className="sidebar-link-label">
          <i className={`bi ${group.icon}`}></i>
          {!isCollapsed && <span>{group.label}</span>}
        </span>
        {!isCollapsed && (
          <i
            className={`bi bi-chevron-${isOpen ? "up" : "down"} fs-070 opacity-80`}
          ></i>
        )}
      </button>

      {isOpen && !isCollapsed && (
        <div className="sidebar-submenu">
          {group.sections
            .filter((section) => section.items.length > 0)
            .map((section, i) => (
              <div key={section.title || i} className="sidebar-section">
                {section.title && (
                  <div className="sidebar-section-label">{section.title}</div>
                )}
                {section.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`sidebar-link sidebar-sublink ${isItemActive(item, path, search) ? "active" : ""}`}
                    aria-current={
                      isItemActive(item, path, search) ? "page" : undefined
                    }
                  >
                    <span className="flex-grow-1">{item.label}</span>
                    {item.badge > 0 && (
                      <span
                        className="sidebar-badge"
                        aria-label={`${item.badge} pending`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const { pathname, search } = useLocation();
  const {
    canSeeApprovals,
    pendingTotal,
    validationCount,
    overtimeCount,
    correctionCount,
    clientVisitCount,
  } = useApprovalAccess();
  const { canManageAssets } = useAssetAccess();
  const leaveAccess = useLeaveAccess();
  const performanceAccess = usePerformanceAccess();
  // Strict: only GET /permissions/me decides what shows (role names grant nothing)
  const { can, companyWide, employeeId } = usePermissions();
  const adminSections = visibleAdminSections(companyWide, can);

  const allGroups = [
    ...(can("employees")
      ? [
          employeesGroup({
            canImport: companyWide("employees", "modify"),
            canSetApprovers: companyWide("employees"),
          }),
        ]
      : []),
    ...(can("leave")
      ? [leaveGroup({ ...leaveAccess, canFile: can("leave", "modify") })]
      : []),
    attendanceGroup({
      can,
      companyWide,
      canSeeApprovals,
      validationCount,
      overtimeCount,
      correctionCount,
      clientVisitCount,
    }),
    ...(canManageAssets ? [assetGroup(companyWide("assets"))] : []),
    ...(can("performance") ? [performanceGroup(performanceAccess)] : []),
    ...(can("learning") ? [LEARNING_GROUP] : []),
    ...(adminSections.length ? [adminGroup(adminSections)] : []),
  ];
  // A group with no links for this user isn't shown
  const groups = allGroups.filter((g) =>
    g.sections.some((sec) => sec.items.length > 0),
  );

  // Groups open automatically when they contain the current page; the user's
  // own open/close choice wins until they navigate elsewhere.
  const [toggled, setToggled] = useState({}); // { [groupKey]: boolean }
  const [toggledAt, setToggledAt] = useState(pathname);
  if (toggledAt !== pathname) {
    setToggled({});
    setToggledAt(pathname);
  }
  const isOpen = (group) =>
    toggled[group.key] ?? isGroupActive(group, pathname, search);

  const toggleGroup = (group) => {
    if (isCollapsed) {
      // Collapsed sidebar: expand it and show this group's links
      setIsCollapsed(false);
      setToggled((prev) => ({ ...prev, [group.key]: true }));
      return;
    }
    setToggled((prev) => ({ ...prev, [group.key]: !isOpen(group) }));
  };

  const linkClass = (active) => `sidebar-link ${active ? "active" : ""}`;

  return (
    <nav
      className={`sidebar-root ${isCollapsed ? "is-collapsed" : ""}`}
      aria-label="Main navigation"
    >
      <div className="sidebar-header">
        {!isCollapsed && (
          <span className="text-slate-500 fs-070 fw-bold text-uppercase">
            Main Navigation
          </span>
        )}
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="btn p-1 d-flex align-items-center justify-content-center sidebar-toggle"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <i className={`bi bi-chevron-${isCollapsed ? "right" : "left"}`}></i>
        </button>
      </div>

      <div className="d-flex flex-column">
        <Link
          to="/dashboard"
          className={linkClass(pathname === "/dashboard")}
          title={isCollapsed ? "Dashboard" : undefined}
        >
          <i className="bi bi-grid-1x2"></i>{" "}
          {!isCollapsed && <span>Dashboard</span>}
        </Link>

        {/* Quick way back to the user's own (My view) dashboard */}
        {employeeId && (
          <Link
            to="/me"
            className={linkClass(false)}
            title={isCollapsed ? "My dashboard" : undefined}
          >
            <i className="bi bi-person-circle"></i>{" "}
            {!isCollapsed && <span>My dashboard</span>}
          </Link>
        )}

        {canSeeApprovals && (
          <Link
            to="/approvals"
            className={linkClass(pathname.startsWith("/approvals"))}
            title={
              isCollapsed ? `Approvals (${pendingTotal} pending)` : undefined
            }
          >
            <i className="bi bi-clipboard-check"></i>
            {!isCollapsed && <span className="flex-grow-1">Approvals</span>}
            {pendingTotal > 0 && (
              <span
                className={`sidebar-badge ${isCollapsed ? "sidebar-badge--dot" : ""}`}
                aria-label={`${pendingTotal} pending`}
              >
                {!isCollapsed && pendingTotal}
              </span>
            )}
          </Link>
        )}

        {groups.map((group) => (
          <NavGroup
            key={group.key}
            group={group}
            path={pathname}
            search={search}
            isOpen={isOpen(group)}
            isCollapsed={isCollapsed}
            onToggle={() => toggleGroup(group)}
          />
        ))}
      </div>
    </nav>
  );
};

export default Sidebar;
